import { getAddress } from "@ethersproject/address";

import { Chains, isEthereum } from "../chain";
import { Service } from "../common";
import { EthAddress, handleHttpError, usdc, ZeroAddress } from "../helpers";
import { Address, Balance, BalancesMap, Integer, SdkError, Token, ZapperToken } from "../types";
import {
  GasPrice,
  VaultTokenMarketData,
  ZapApprovalStateOutput,
  ZapApprovalTransactionOutput,
  ZapOutput,
  ZapProtocol,
} from "../types/custom/zapper";

const ZAPPER_AFFILIATE_ADDRESS = "0xFEB4acf3df3cDEA7399794D0869ef76A6EfAff52";

/**
 * [[ZapperService]] interacts with the zapper api to gather more insight for
 * tokens and user positions.
 */
export class ZapperService extends Service {
  /**
   * Fetch all the tokens supported by the zapper protocol along with some basic
   * metadata.
   * @returns list of tokens supported by the zapper protocol.
   */
  async supportedTokens(): Promise<Token[]> {
    const url = "https://api.zapper.fi/v2/prices";
    const zapperTokens: ZapperToken[] = await fetch(url, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    const network = Chains[this.chainId];

    const visibleZapperTokens = zapperTokens.filter((zapperToken) => !zapperToken.hide);

    return visibleZapperTokens.map((zapperToken): Token => {
      const address = zapperToken.address === ZeroAddress ? EthAddress : getAddress(zapperToken.address);

      return {
        address,
        decimals: String(zapperToken.decimals),
        // addresses in the icon url should always be lowercase to be fetched correctly
        icon: `https://assets.yearn.network/tokens/${network}/${zapperToken.address}.png`,
        name: zapperToken.symbol,
        priceUsdc: usdc(zapperToken.price),
        dataSource: "zapper",
        supported: { zapper: true },
        symbol: zapperToken.symbol,
      };
    });
  }

  /**
   * Fetch token balances from the {@link ZapperService.supportedTokens} list
   * for a particular address.
   * @param address
   */
  async balances<T extends Address>(address: T): Promise<Balance[]>;

  /**
   * Fetch token balances from the {@link ZapperService.supportedTokens} list
   * for a list of addresses.
   * @param addresses
   */
  async balances<T extends Address>(addresses: T[]): Promise<BalancesMap<T>>;
  async balances<T extends Address>(addresses: T[] | T): Promise<BalancesMap<T> | Balance[]>;

  async balances<T extends Address>(addresses: T[] | T): Promise<BalancesMap<T> | Balance[]> {
    const url = "https://api.zapper.fi/v2/apps/tokens/balances";
    const params = new URLSearchParams({
      "addresses[]": Array.isArray(addresses) ? addresses.join() : addresses,
    });
    const { balances } = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());
    Object.keys(balances).forEach((address) => {
      const copy = balances[address];
      delete balances[address];
      if (copy.products.length === 0) {
        balances[getAddress(address)] = [];
      } else {
        const assets = copy.products[0].assets;
        balances[getAddress(address)] = assets.map((balance: Record<string, string>): Balance => {
          const address = balance.address === ZeroAddress ? EthAddress : getAddress(String(balance.address));
          return {
            address,
            token: {
              address,
              name: balance.symbol,
              symbol: balance.symbol,
              decimals: balance.decimals,
            },
            balance: balance.balanceRaw,
            balanceUsdc: usdc(balance.balanceUSD),
            priceUsdc: usdc(balance.price),
          };
        });
      }
    });
    if (!Array.isArray(addresses)) return balances[addresses];
    return balances;
  }

  /**
   * Fetches vault token market data for the Yearn application
   * @returns list of zapper supported vault addresses
   */
  async supportedVaultAddresses(): Promise<Address[]> {
    if (!isEthereum(this.chainId)) {
      throw new SdkError(`Only Ethereum is supported for token market data, got ${this.chainId}`);
    }

    const url = "https://api.zapper.fi/v2/apps/yearn/tokens";
    const params = new URLSearchParams({
      network: "ethereum",
      groupId: "vault",
    });

    const vaultTokenMarketData = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return vaultTokenMarketData
      .map((vaultTokenMarketData: VaultTokenMarketData) => getAddress(vaultTokenMarketData.address))
      .filter((address: string) => !!address);
  }

  /**
   * Fetch up to date gas prices in gwei
   * @returns gas prices
   */
  async gas(): Promise<GasPrice> {
    const url = "https://api.zapper.fi/v2/gas-prices";
    const params = new URLSearchParams({
      eip1559: "false",
    });
    const gas = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());
    return gas;
  }

  /**
   * Fetches the data needed to check token ZapIn contract approval state
   * @param from - the address that is depositing
   * @param token - the token to be sold to pay for the deposit
   * @param zapProtocol the protocol to use with zapper e.g. Yearn, Pickle
   */
  async zapInApprovalState(
    from: Address,
    token: Address,
    zapProtocol: ZapProtocol = ZapProtocol.YEARN
  ): Promise<ZapApprovalStateOutput> {
    const url = `https://api.zapper.fi/v2/zap-in/vault/${zapProtocol}/approval-state`;
    const params = new URLSearchParams({
      ownerAddress: from,
      sellTokenAddress: token,
    });
    const response: ZapApprovalStateOutput = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return response;
  }

  /**
   * Fetches the data needed to approve ZapIn Contract for a token
   * @param from - the address that is depositing
   * @param token - the token to be sold to pay for the deposit
   * @param gasPrice
   * @param zapProtocol the protocol to use with zapper e.g. Yearn, Pickle
   */
  async zapInApprovalTransaction(
    from: Address,
    token: Address,
    gasPrice: Integer,
    zapProtocol: ZapProtocol = ZapProtocol.YEARN
  ): Promise<ZapApprovalTransactionOutput> {
    const url = `https://api.zapper.fi/v2/zap-in/vault/${zapProtocol}/approval-transaction`;
    const params = new URLSearchParams({
      gasPrice,
      ownerAddress: from,
      sellTokenAddress: token,
    });
    const response: ZapApprovalTransactionOutput = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return response;
  }

  /**
   * Fetches the data needed to check token ZapOut contract approval state
   * @param from - the address that is withdrawing
   * @param token - the vault token to be withdrawn
   * @param zapProtocol the protocol to use with zapper e.g. Yearn, Pickle
   */
  async zapOutApprovalState(
    from: Address,
    token: Address,
    zapProtocol: ZapProtocol = ZapProtocol.YEARN
  ): Promise<ZapApprovalStateOutput> {
    const url = `https://api.zapper.fi/v2/zap-out/vault/${zapProtocol}/approval-state`;
    const params = new URLSearchParams({
      ownerAddress: from,
      sellTokenAddress: token,
    });
    const response: ZapApprovalStateOutput = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return response;
  }

  /**
   * Fetches the data needed to approve ZapOut Contract for a token
   * @param from - the address that is withdrawing
   * @param token - the vault token to be withdrawn
   * @param gasPrice
   * @param zapProtocol the protocol to use with zapper e.g. Yearn, Pickle
   */
  async zapOutApprovalTransaction(
    from: Address,
    token: Address,
    gasPrice: Integer,
    zapProtocol: ZapProtocol = ZapProtocol.YEARN
  ): Promise<ZapApprovalTransactionOutput> {
    const url = `https://api.zapper.fi/v2/zap-out/vault/${zapProtocol}/approval-transaction`;
    const params = new URLSearchParams({
      gasPrice,
      ownerAddress: from,
      sellTokenAddress: token,
    });
    const response: ZapApprovalTransactionOutput = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return response;
  }

  /**
   * Fetches the data needed to zap into a vault
   * @param from - the address that is depositing
   * @param token - the token to be sold to pay for the deposit
   * @param amount - the amount of tokens to be sold
   * @param vault - the vault to zap into
   * @param gasPrice
   * @param slippagePercentage - slippage as a decimal
   * @param skipGasEstimate - provide the gasLimit in the response. Should be set to true when simulating a zap without approval
   * @param zapProtocol the protocol to use with zapper e.g. Yearn, Pickle
   */
  async zapIn(
    from: Address,
    token: Address,
    amount: Integer,
    vault: Address,
    gasPrice: Integer,
    slippagePercentage: number,
    skipGasEstimate: boolean,
    zapProtocol: ZapProtocol = ZapProtocol.YEARN,
    partnerId?: string
  ): Promise<ZapOutput> {
    let sellToken = token;
    if (EthAddress === token) {
      // If Ether is being sent, the sellTokenAddress should be the zero address
      sellToken = ZeroAddress;
    }

    const url = `https://api.zapper.fi/v2/zap-in/vault/${zapProtocol}/transaction`;
    const params = new URLSearchParams({
      affiliateAddress: ZAPPER_AFFILIATE_ADDRESS,
      ownerAddress: from,
      sellTokenAddress: sellToken,
      sellAmount: amount,
      poolAddress: vault,
      gasPrice: gasPrice,
      slippagePercentage: slippagePercentage.toString(),
      skipGasEstimate: skipGasEstimate ? "true" : "false",
      ...((partnerId && { partnerId }) || {}),
    });

    const response: ZapOutput = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return response;
  }

  /**
   * Fetches the data needed to zap out of a vault
   * @param from - the address that is withdrawing
   * @param token - the token that'll be received
   * @param amount - the amount of tokens to sell
   * @param vault - the vault to zap out of
   * @param gasPrice
   * @param slippagePercentage - slippage as a decimal
   * @param skipGasEstimate - provide the gasLimit in the response. Should be set to true when simulating a zap without approval
   * @param zapProtocol the protocol to use with zapper e.g. Yearn, Pickle
   * @param signature - the account valid secp256k1 signature of Permit encoded from r, s, v. (https://eips.ethereum.org/EIPS/eip-2612)
   */
  async zapOut(
    from: Address,
    token: Address,
    amount: Integer,
    vault: Address,
    gasPrice: Integer,
    slippagePercentage: number,
    skipGasEstimate: boolean,
    zapProtocol: ZapProtocol = ZapProtocol.YEARN,
    signature?: string
  ): Promise<ZapOutput> {
    let toToken = token;
    if (EthAddress === token) {
      // If Ether is being received, the toTokenAddress should be the zero address
      toToken = ZeroAddress;
    }

    const url = `https://api.zapper.fi/v2/zap-out/vault/${zapProtocol}/transaction`;
    const params = new URLSearchParams({
      affiliateAddress: ZAPPER_AFFILIATE_ADDRESS,
      ownerAddress: from,
      toTokenAddress: toToken,
      sellAmount: amount,
      poolAddress: vault,
      gasPrice: gasPrice,
      slippagePercentage: slippagePercentage.toString(),
      skipGasEstimate: skipGasEstimate ? "true" : "false",
      ...(signature && { signature }),
    });
    const response: ZapOutput = await fetch(`${url}?${params}`, {
      headers: { Authorization: `Basic ${this.ctx.zapper}` },
    })
      .then(handleHttpError)
      .then((res) => res.json());

    return response;
  }
}
